import type { FindingOut, RunFindingsSummaryOut } from '@/api/types';

/** Per-disposition-status recovery dollars for one outcome category. The
 *  four status buckets sum exactly to `total`. */
export interface OutcomeRecoveryTotals {
  total: number;
  approved: number;
  needsReview: number;
  pending: number;
  rejected: number;
}

export interface RunRecoverySummary {
  recovery: OutcomeRecoveryTotals;
  compliance: OutcomeRecoveryTotals;
}

interface CentsTotals {
  total: number;
  approved: number;
  needsReview: number;
  pending: number;
  rejected: number;
}

function blankCents(): CentsTotals {
  return { total: 0, approved: 0, needsReview: 0, pending: 0, rejected: 0 };
}

function toDollars(c: CentsTotals): OutcomeRecoveryTotals {
  return {
    total: c.total / 100,
    approved: c.approved / 100,
    needsReview: c.needsReview / 100,
    pending: c.pending / 100,
    rejected: c.rejected / 100,
  };
}

/** Map the server-computed run summary (`GET /findings/summary`) to the
 *  funnel shape the run header renders. Preferred over
 *  `summarizeRunRecovery`: the server aggregates in SQL across the whole
 *  run, so the cards are correct regardless of how many findings the
 *  browser has loaded (the client-side sum silently under-counted when a
 *  run's findings were paginated / partially loaded). Recovery totals are
 *  serialized as decimal strings; `Number(...)` with a 0 fallback keeps a
 *  malformed value from poisoning the sum. */
export function summaryToRecoveryTotals(s: RunFindingsSummaryOut): RunRecoverySummary {
  const n = (x: string): number => {
    const parsed = Number(x);
    return Number.isFinite(parsed) ? parsed : 0;
  };
  const recovery: OutcomeRecoveryTotals = {
    approved: n(s.approved.recovery_total),
    needsReview: n(s.needs_review.recovery_total),
    pending: n(s.pending.recovery_total),
    rejected: n(s.rejected.recovery_total),
    total:
      n(s.approved.recovery_total) +
      n(s.needs_review.recovery_total) +
      n(s.pending.recovery_total) +
      n(s.rejected.recovery_total),
  };
  const compliance: OutcomeRecoveryTotals = {
    approved: n(s.compliance_approved.recovery_total),
    needsReview: n(s.compliance_needs_review.recovery_total),
    pending: n(s.compliance_pending.recovery_total),
    rejected: n(s.compliance_rejected.recovery_total),
    total: n(s.compliance_total),
  };
  return { recovery, compliance };
}

/**
 * Recovery dollars for a run, split by outcome (recovery vs compliance-risk)
 * and disposition status, summed from its findings.
 *
 * Mirrors the server's run totals — a naive cross-currency SUM of the
 * override-aware recovery amount (`finding.recovery.amount` already reflects
 * an override), grouped by outcome. Accumulated in integer cents so the four
 * status rows sum exactly to the total with no float drift.
 */
export function summarizeRunRecovery(
  findings: readonly FindingOut[],
): RunRecoverySummary {
  const recovery = blankCents();
  const compliance = blankCents();

  for (const f of findings) {
    const cents = Math.round(Number(f.recovery.amount) * 100);
    if (!Number.isFinite(cents)) continue;
    const bucket = f.outcome === 'compliance_risk' ? compliance : recovery;
    bucket.total += cents;
    if (f.status === 'approved') bucket.approved += cents;
    else if (f.status === 'needs_review') bucket.needsReview += cents;
    else if (f.status === 'pending') bucket.pending += cents;
    else if (f.status === 'rejected') bucket.rejected += cents;
  }

  return { recovery: toDollars(recovery), compliance: toDollars(compliance) };
}
